import EventEmitter from "../EventEmitter";

export type Fragment = [number, number];
import { easePolyIn } from "d3-ease";
import { secondsToMilliseconds } from "date-fns";

import { events } from "@/utils";

import { fragmentEnded } from "./events";

const ZERO_SECOND_SILENCE =
  "data:audio/wav;base64,UklGRigAAABXQVZFZm10IBIAAAABAAEARKwAAIhYAQACABAAAABkYXRhAgAAAAEA";

export type SimpleAudioState = {
  fragments: Fragment[];
  loaded: boolean;
  stalled: boolean;
  errored: boolean;
  currentTime: number;
  playing: boolean;
  paused: boolean;
  loading: boolean;
  loadeddata: boolean;
  src: string;
  stopped: boolean;
  unloaded: boolean;
};
const VOLUME_MIN_VALUE = 0.01;

type SimpleAudioEvents = keyof HTMLMediaElementEventMap | "fragmentEnded";

/**
 * Audio player, uses the standard HTML5 audio element but adds some functionality:
 * - Observer-pattern for eventlisteners
 * - Fragmentation of parts of the audio clip. Defining what's playable and not.
 * - Fade in/out
 */

class SimpleAudio extends EventEmitter<SimpleAudioState> {
  playerTimeout: null | ReturnType<typeof setTimeout> = null;
  settings = {
    FADE_OUT_DURATION: 150, //ms
    FADE_IN_DURATION: 250, //ms
  };

  audio = new Audio();

  private setupFragments = () => {
    const clear = () => this.playerTimeout && clearTimeout(this.playerTimeout);
    const setup = () => {
      clear();
      const next = this.getNextFragment();
      const [start] = this.getCurrentFragment() || [];

      this.playerTimeout = setTimeout(
        () => {
          if (this.state.unloaded) return null;
          this.pause().then(() => {
            this.audio.dispatchEvent(fragmentEnded);

            if (next) {
              this.skipToNextFragment();
            } else {
              if (start !== undefined) {
                this.audio.currentTime = start;
              }
            }
          });
        },
        secondsToMilliseconds(this.timeLeftOfFragment()) -
          this.settings.FADE_OUT_DURATION
      ); // milliseconds
    };
    // When seeking or playing we have to set up the listener.
    this.listen("play", setup);
    this.listen("seeked", setup);
    // ... and when stopping we clear it.
    this.listen("pause", clear);
    this.listen("stalled", clear);
  };

  public skipToPreviousFragment = () => {
    const previous = this.getPreviousFragment();

    if (previous) {
      const [previousStart] = previous;
      this.play(previousStart);
    }
  };

  public skipToNextFragment = () => {
    const next = this.getNextFragment();

    if (next) {
      const [nextStart] = next;
      this.play(nextStart);
    }
  };

  private async fade({
    from = undefined,
    to,
    duration,
    ease,
  }: {
    from?: number;
    to: number;
    duration: number;
    ease?: (t: number) => number;
  }) {
    const audio = this.audio;
    const start = new Date().valueOf();
    // Math.max is used here because firefox has a bug with playback of muted sound.
    const currentVolume = Math.max(VOLUME_MIN_VALUE, from ?? audio.volume);
    const getDelta = () => Math.abs(new Date().valueOf() - start);
    const totalChange = to - currentVolume;

    // use delta time to figure out how much of a fade we should do.
    return new Promise<void>((resolve) => {
      // Fade using delta-time so that if the browser whacks out we still get the fade finished within the time-frame
      // The fade will be a bit harsher if this happens, but I think that's better than it lagging behind.
      // Technically the lenght of the fade will deviate slightly due to framerate.
      const handleFade = () => {
        const timeDelta = getDelta();
        const passed = Math.max(
          VOLUME_MIN_VALUE,
          Math.min(1, timeDelta / duration)
        );
        const position = ease ? ease(passed) : passed; // deafult to linear easing
        const volume = currentVolume + totalChange * position;
        audio.volume = volume;

        if (volume !== to) {
          window.requestAnimationFrame(handleFade);
        } else {
          resolve();
        }
      };
      // start listening.
      window.requestAnimationFrame(handleFade);
    });
  }

  // Perform a check for whether the audio can actually be played for specific time within any of the given ranges
  public canPlayAt = (time: number) => {
    const { fragments } = this.state;

    // No fragments means we can play the whole clip.
    if (fragments.length === 0) return true;
    return fragments.some(([start, stop]) => start <= time && time <= stop);
  };

  // Calculates the time left until the fragment ends.
  public timeLeftOfFragment = () => {
    const currentTime = this.audio.currentTime;
    const { fragments } = this.state;
    const currentFragment = fragments.find(
      ([start, stop]) => start <= currentTime && currentTime <= stop
    );

    if (!currentFragment) {
      // There is no fragment playing at the current timestamp, so return 0.
      return 0;
    }

    const [_start, stop] = currentFragment;
    return stop - currentTime;
  };

  public getCurrentFragment = () => {
    const { currentTime } = this.audio;
    const { fragments } = this.state;
    const current = fragments.find(
      ([start, stop]) => start <= currentTime && currentTime <= stop
    );
    return current;
  };

  // Get the previous fragment or null
  public getPreviousFragment = (): null | Fragment => {
    const { fragments } = this.state;
    const currentFragment = this.getCurrentFragment();

    const current = fragments.findIndex(
      (fragment) => currentFragment === fragment
    );
    return current - 1 < 0 ? null : fragments[current - 1];
  };

  // Get the next fragment or null
  public getNextFragment = (): null | Fragment => {
    const { fragments } = this.state;
    const currentFragment = this.getCurrentFragment();

    const current = fragments.findIndex(
      (fragment) => currentFragment === fragment
    );
    return current + 1 >= fragments.length ? null : fragments[current + 1];
  };

  /**
   * Set up the most basic listeners for the audio,
   * more listeners can be added by directly accessing the .audio of the class.
   */
  private setupListeners = () => {
    this.listen("loadstart", () => this.set("loading", true), true);

    this.listen(
      "loadeddata",
      () => this.batch({ loadeddata: true, loading: false }),
      true
    );
    this.listen("error", () => this.set("errored", true), true);
    this.listen(
      "stalled",
      () => this.batch({ stalled: true, playing: false, loading: true }),
      false
    );
    this.listen("ended", () => this.set("playing", false));
    this.listen("timeupdate", () => {
      // seeking to the zero-length audio won't work. It'll be capped to the length of the zero-length audio,
      // so in that case we just set the state without caring for the audio.
      if (!this.state.unloaded) {
        this.set("currentTime", this.audio.currentTime);
      }
    });

    this.listen("play", () => {
      this.batch({
        playing: true,
        stalled: false,
        paused: false,
        stopped: false,
      });

      if (!this.canPlayAt(this.audio.currentTime)) {
        this.audio.pause();
      }
    });

    this.listen("pause", () => {
      this.batch({ playing: false, paused: true, stopped: false });
    });
  };

  // Unlisten to an event
  unlisten<K extends SimpleAudioEvents>(event: K, fn: EventListener) {
    this.audio.removeEventListener(event, fn);
  }

  // Listen to an event - Small wrapper for easier handling of listeners
  listen<K extends SimpleAudioEvents>(
    event: K,
    fn: EventListener,
    once = false
  ) {
    this.audio.addEventListener(event, fn, { once });
  }

  // Load the audio. return early if already loaded
  private load = () => {
    return new Promise<void>((resolve, reject) => {
      const { loadeddata, fragments, src } = this.state;
      // already loaded - resolve instantly!
      if (!this.canPlayAt(this.audio.currentTime)) {
        this.restart();
      }
      if (loadeddata && this.state.unloaded === false) {
        resolve();
      } else {
        // setup listeners
        this.listen(
          "loadeddata",
          () => {
            resolve();
            this.set("unloaded", false);
          },
          true
        );
        this.listen("error", () => reject(), true);

        // Setup fragments if there are any
        if (fragments.length > 0) {
          this.setupFragments();
        }

        // setup src
        this.audio.src = src || "";
        // Explicitly load for Safari
        this.audio.load();
      }
    });
  };

  // Instantly stops the loading of a clip without throwing an error.
  public unload = () => {
    this.audio.pause(); // This one pauses instantly and does not care about fading.
    this.audio.src = ZERO_SECOND_SILENCE;
    this.batch({ playing: false, loaded: false, unloaded: true });
  };

  private setupGlobalInterfaces = () => {
    // Listen to stop events
    document.addEventListener("stopAllPlayingAudio", this.stop);

    // Make sure browser controls work for the currently playing piece.
    if (navigator && navigator.mediaSession) {
      navigator.mediaSession.setActionHandler("play", async () => {
        try {
          this.play();
        } catch {
          // silence any error here. We don't really care about them.
        }
      });
      navigator.mediaSession.setActionHandler("pause", async () => {
        try {
          this.pause();
        } catch {
          // silence any error here. We don't really care about them.
        }
      });
    }
  };

  // Undo whatever is done in setupGlobalInterfaces
  private tearDownGlobalInterfaces = () => {
    document.removeEventListener("stopAllPlayingAudio", this.stop);

    // Empty out the action handlers
    if (navigator && navigator.mediaSession) {
      navigator.mediaSession.setActionHandler("play", null);
      navigator.mediaSession.setActionHandler("pause", null);
    }
  };

  private restart = () => {
    const { fragments } = this.state;
    // Set the currentTime to be the first fragment ( if there are any )
    if (fragments && fragments.length > 0) {
      this.audio.currentTime = fragments[0][0];
    }
  };

  public play = async (time?: number) => {
    // Dispatch an event that stops all playing audio, make sure we're not stopping _this_ one.
    document.removeEventListener("stopAllPlayingAudio", this.stop);
    document.dispatchEvent(events.stopAllPlayingAudio);

    // Wait for the source to be loaded
    await this.load();

    // Seek to the correct time (if a time was supplied)
    if (typeof time === "number") {
      this.audio.currentTime = time;
    }

    this.setupGlobalInterfaces();
    // Play and then setup fade in
    return this.audio.play().then(() =>
      this.fade({
        from: 0,
        to: 1,
        ease: easePolyIn,
        duration: this.settings.FADE_IN_DURATION,
      })
    );
  };

  public pause = async () => {
    return this.fade({ to: 0, duration: this.settings.FADE_OUT_DURATION }).then(
      () => {
        this.audio.pause();
      }
    );
  };

  /** It's not necessary to add seek onto the class, we could just use audio.seek, but
   * since play,stop and pause are on the class level the API will be a little friendlier
   * this way.
   */
  public seek = (to: number) => {
    if (this.state.unloaded) {
      this.set("currentTime", to);
      return;
    }
    this.audio.currentTime = to;
    // Force a seek/timeupdate event to happen, otherwise this will not trigger unless playing
    this.audio.dispatchEvent(new Event("seeked"));
    this.audio.dispatchEvent(new Event("timeupdate"));
  };

  public stop = () => {
    return new Promise<void>((resolve) => {
      this.tearDownGlobalInterfaces();
      // pause gracefully before unloading
      this.pause().then(() => {
        this.unload();
        resolve();
      });
    });
  };

  constructor(state: Partial<SimpleAudioState>) {
    super({
      fragments: [],
      loaded: false,
      errored: false,
      stalled: false,
      unloaded: false,
      playing: false,
      loading: false,
      paused: false,
      currentTime: 0,
      loadeddata: false,
      src: "",
      stopped: false,
      ...state,
    });

    // setup the start time
    this.restart();

    this.audio.preload = "metadata";
    this.setupListeners();
  }
}

export default SimpleAudio;
